import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { addItem, updateItem } from "../../store/slices/defectSlice";
import { useFormik } from "formik";
import { Defect } from "../../types/defect";
import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { getDefectEditableFields } from "../../utils/defectTableHelper";
import { FormDropdown } from "../Shared/FormDropdown";
import { FormInputTextarea } from "../Shared/FormInputTextarea";
import { addDefect, updateDefect } from "../../services/defectService";
import { GradingAreaMapping } from "../../config/GradingAreaMapping";
import { FormEstimateDefectCost } from "./FormEstimateDefectCost";
import { FormMultiSelect } from "../Shared/FormMultiSelect";
import {
    selectInspectionEnableLowPriorityDefects,
    selectPhotoFolders
} from "../../store/selectors/inspectionSelectors";
import { FormDefectCode } from "./FormDefectCode";

type Props = {
    defect: Defect;
};

export const DefectForm = (props: Props) => {
    const { defect } = props;

    const dispatch = useAppDispatch();

    const photoFolders = useAppSelector(selectPhotoFolders);
    const enableLowPriorityDefects = useAppSelector(
        selectInspectionEnableLowPriorityDefects
    );

    const [loading, setLoading] = useState(false);

    const formik = useFormik<Defect>({
        initialValues: defect,
        validate: (data) => {
            let errors: Record<string, unknown> = {};

            if (!data.priority) {
                errors.priority = "Priority is required.";
            }

            if (!data.description) {
                errors.description = "Description is required.";
            }

            if (!data.recommendations) {
                errors.recommendations = "Recommendations is required.";
            }

            return errors;
        },
        onSubmit: async (data) => {
            setLoading(true);

            if (defect.defectId) {
                const updated = await updateDefect(data);

                if (updated) {
                    dispatch(updateItem(data));
                }
            } else {
                const added = await addDefect(data);

                if (added) {
                    dispatch(addItem(added));
                }
            }

            setLoading(false);
        }
    });

    const editableFields = useMemo(
        () => getDefectEditableFields(Boolean(defect.key && defect.rawAnswer)),
        []
    );

    const gradingAreaOptions = useMemo(
        () =>
            Object.keys(GradingAreaMapping).map((key) => ({
                label: GradingAreaMapping[key],
                value: key
            })),
        []
    );

    const priorityOptions = useMemo(() => {
        const options = [
            { label: "High", value: 2 },
            { label: "Medium", value: 3 }
        ];

        if (enableLowPriorityDefects) {
            options.push({ label: "Low", value: 5 });
        }

        return options;
    }, [enableLowPriorityDefects]);

    const photoFolderOptions = useMemo(
        () =>
            photoFolders.map((photoFolder) => ({
                label: photoFolder.Name,
                value: photoFolder.Id.toString()
            })),
        []
    );

    return (
        <div className="flex pt-2">
            <form
                onSubmit={formik.handleSubmit}
                className="flex w-full flex-column"
            >
                <FormDefectCode
                    disabled={!editableFields.includes("typeCode")}
                    formik={formik}
                />
                <FormDropdown
                    disabled={!editableFields.includes("priority")}
                    formik={formik}
                    value="priority"
                    label="Priority"
                    options={priorityOptions}
                />
                <FormInputTextarea
                    disabled={!editableFields.includes("description")}
                    formik={formik}
                    value="description"
                    label="Description"
                />
                <FormInputTextarea
                    disabled={!editableFields.includes("recommendations")}
                    formik={formik}
                    value="recommendations"
                    label="Recommendations"
                />
                <FormMultiSelect
                    disabled={!editableFields.includes("gradingArea")}
                    formik={formik}
                    value="gradingArea"
                    label="Grading Area"
                    options={gradingAreaOptions}
                    splitter=";"
                />
                <FormEstimateDefectCost
                    disabled={
                        !editableFields.includes("estimateDefectCostRangeId")
                    }
                    formik={formik}
                    value="estimateDefectCostRangeId"
                />
                <FormMultiSelect
                    disabled={!editableFields.includes("photoFolder")}
                    formik={formik}
                    value="photoFolder"
                    label="Photo Folders"
                    options={photoFolderOptions}
                    splitter=";"
                />
                <Button
                    icon={loading ? "pi pi-spin pi-spinner" : undefined}
                    type="submit"
                    label={loading ? undefined : "Submit"}
                    disabled={loading}
                    className="w-full mt-2"
                />
            </form>
        </div>
    );
};
